import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Effect, pipe } from 'effect'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentGame, useGetCurrentLanguage } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { getRacesByIds } from 'src/services/supabase/queries'
import type { Race } from 'src/types/domain/Race'
import { useViewportScale, transformPresets } from 'src/hooks/useViewportScale'
import type { CountingOutcome } from 'src/lib/fairness/mappers'
import { BirdieFairnessBody } from 'src/components/BirdieBoard/BirdieFairnessBody'
import { verifierLink } from 'src/components/BirdieBoard/pastRounds'
import {
  countingBranchDraw,
  deriveRoundVerification,
  formatTimeAgo,
  formatWeightPercent,
  getMultiplierTier,
  selectRoundFairness,
  shortenHash,
  type CountingReveal,
  type MarbleReveal,
  type RoundDerivation
} from './crashRoundFairness'
import './crash-round-modal.scss'

const RESET_COPIED_MS = 2000

// The seed-reveal poll (v35): while the round is settled but unrevealed,
// re-fetch the race row every 4s, up to ~40s — the reveal rides the settle
// pipeline and normally lands within seconds; the cap keeps a genuinely
// stuck round from polling forever.
const REVEAL_POLL_MS = 4000
const REVEAL_POLL_MAX_TRIES = 10

type Props = Readonly<{
  race: Race
  onClose: () => void
  // Harness-only: render the sheet in-flow (no portal, no fixed backdrop,
  // no focus stealing) so the dev page can pin multiple variants side by
  // side for capture. NEVER set in production paths.
  inline?: boolean
  // Harness-only: open the provably-fair explainer on mount (capture the
  // expanded state without scripting a click).
  defaultExplainerOpen?: boolean
}>

// Clipboard write with the execCommand fallback for embedded/iframe
// contexts where the async Clipboard API is blocked (same pattern the old
// provably-fair badge used).
async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value)
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = value
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    try {
      document.execCommand('copy')
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

// One copy-on-click value row: shortened display, full value to clipboard,
// "Copied" affordance.
function CopyValue({
  label,
  value,
  copiedText,
  copyLabel
}: Readonly<{ label: string; value: string; copiedText: string; copyLabel: string }>) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), RESET_COPIED_MS)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopy = useCallback(() => {
    void copyText(value)
    setCopied(true)
  }, [value])

  return (
    <button
      type="button"
      className="crash-round-modal__value-row"
      onClick={handleCopy}
      title={copyLabel}
    >
      <span className="crash-round-modal__value-label">{label}</span>
      <code className="crash-round-modal__value">{shortenHash(value)}</code>
      <span className={`crash-round-modal__copy ${copied ? 'is-copied' : ''}`}>
        {copied ? copiedText : copyLabel}
      </span>
    </button>
  )
}

// Section eyebrow — the panel's tick-flanked letter-spaced caps (the crash
// zone cards' `__label--eyebrow` treatment), so the modal reads as the same
// game chrome, not a generic dialog.
function SectionEyebrow({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="crash-round-modal__eyebrow">{children}</div>
}

/**
 * Provably-fair round sheet (v12.4 ask 8 overhaul), opened from a history
 * pill, a past-games row or a bet-history entry. Deep-green glass in the
 * crash panel's design language; Aviator's round-modal STRUCTURE carrying
 * OUR commit/reveal fields:
 *
 *   header     round short-id + the round's result chip + when
 *   commitment the pre-round sha256 of the seed — published before betting
 *              closed, and itself a link in a pre-published hash chain, so it
 *              binds the OUTCOME and not merely the seed
 *   reveal     the server seed (+ chain root / index) from the race_settled
 *              fairness payload
 *   derivation the recomputation, CLIENT-SIDE with WebCrypto, with a live
 *              verified/mismatch verdict against the published result
 *   explainer  "What is provably fair?" — expandable, truthful description
 *              of OUR scheme (commit → reveal → recompute; NO player seeds)
 *
 * GAME-AWARE. The sheet dispatches on `fairness.game` from the REVEAL, never
 * on the widget's game-type predicates (`crash` sits inside CCTV_GAME_TYPES
 * for music suppression alone, so isCCTVGameType is not a fairness signal):
 *   crash               → hex / decimal r / multiplier
 *   vehicle_boundaries  → outcome draw / published weights / offset draw /
 *                         the camera-measured final count / the boundaries
 *   marble_order        → the single "order" draw / (weighted) published
 *                         weights / the seed-derived finishing order (1st..Kth)
 *   birdie              → the Birdie board's OWN fairness body (B33):
 *                         commitment, reveal, the card-and-catalogue terms
 *                         and the hand-off to the public verifier — mounted
 *                         inside THIS chassis, the same component the Birdie
 *                         sheet mounts, so the three entry points cannot
 *                         drift. NO recompute (the verifier's Birdie mappers
 *                         land in M6), therefore no verdict of any kind.
 * Counting, marble and Birdie rounds have NO house edge to show — those
 * families keep their margin in the payout odds table, never in the RNG.
 *
 * SECURITY: only revealed (settled) data ever renders — the commitment is
 * the sole pre-round artifact; the derivation runs on the reveal, so it
 * cannot exist before the outcome is public.
 *
 * Desktop small-iframe scaling: the dialog consumes the widget's viewport
 * scale factor (useViewportScale, basic preset) — the modal portals to
 * <body>, outside every scaled container, so it must scale itself.
 *
 * Accessibility: role=dialog + aria-modal, Esc and backdrop close, minimal
 * focus trap, focus restored to the opener on unmount. Values copy on click.
 */
export function CrashRoundModal({ race, onClose, inline, defaultExplainerOpen }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const dialogRef = useRef<HTMLDivElement>(null)
  const [explainerOpen, setExplainerOpen] = useState(defaultExplainerOpen ?? false)
  const [derivation, setDerivation] = useState<RoundDerivation | null>(null)

  // v35 — LIVE fairness (Kasper: the seed "needs a refresh to show up").
  // The `race` prop is a SNAPSHOT from open time; the revealed seed rides
  // the race_settled payload into the game store over the socket AFTER the
  // modal opened. Three sources, best wins (first with a revealed seed):
  //   1. a direct row fetch (getRacesByIds — belt-and-braces for a missed
  //      socket event / stale history after a reload), polled every 4s
  //      while the round is settled but unrevealed, capped;
  //   2. the LIVE store race (subscribed — streams in without a reopen);
  //   3. the prop snapshot.
  const liveGame = useGetCurrentGame()
  const liveRace = isMarbleGame(liveGame)
    ? liveGame.races.find((r) => r.uuid === race.uuid)
    : undefined
  const [fetchedRace, setFetchedRace] = useState<Race | null>(null)
  const infoProp = selectRoundFairness(race)
  const infoLive = liveRace ? selectRoundFairness(liveRace) : null
  const infoFetched = fetchedRace ? selectRoundFairness(fetchedRace) : null
  // Best source with a revealed seed wins; with NO seed anywhere, prefer a
  // SETTLED source (review: a store race that missed the race_settled
  // socket frame reports settled=false — preferring it over the settled
  // prop showed pre-settle copy AND kept the poll gate closed, disabling
  // the belt-and-braces in exactly its target scenario).
  const info =
    [infoFetched, infoLive, infoProp].find((i) => i?.serverSeed) ??
    [infoFetched, infoLive, infoProp].find((i) => i?.settled) ??
    infoLive ??
    infoProp

  const pollTriesRef = useRef(0)
  useEffect(() => {
    if (info.serverSeed || !info.settled) return undefined
    let cancelled = false
    const fetchOnce = async () => {
      if (pollTriesRef.current >= REVEAL_POLL_MAX_TRIES) return
      pollTriesRef.current += 1
      const races = await pipe(
        getRacesByIds({ ids: [race.uuid], limit: 1 }),
        Effect.orElseSucceed(() => [] as Race[]),
        Effect.runPromise
      )
      if (!cancelled && races[0]) setFetchedRace(races[0])
    }
    void fetchOnce()
    const id = setInterval(() => void fetchOnce(), REVEAL_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.serverSeed, info.settled, race.uuid])

  // The modal portals to document.body — outside every viewport-scaled
  // wrapper — so it applies the widget's desktop scale factor itself
  // (no-ops on mobile, where the sheet is a bottom drawer anyway).
  const scaleStyle = useViewportScale({
    transform: transformPresets.basic,
    transformOrigin: 'center'
  })

  // Recompute the round from the revealed seed (WebCrypto, async). Settled
  // rounds only — the seed IS the input, so this cannot run pre-reveal.
  const {
    game,
    serverSeed,
    nonce,
    observedCommitment,
    schemeVersion,
    chainRootHash,
    chainIndex,
    beacon,
    multiplier,
    houseEdge,
    settled
  } = info
  // `info.counting` is a fresh object on every render (selectRoundFairness is
  // pure, not memoised), so it cannot go into the effect's deps directly —
  // that would re-derive → setState → re-render, forever. Key on its VALUE.
  const countingKey = info.counting ? JSON.stringify(info.counting) : ''
  const counting = useMemo<CountingReveal | undefined>(
    () => (countingKey ? (JSON.parse(countingKey) as CountingReveal) : undefined),
    [countingKey]
  )
  // Same value-keying for the marble reveal — a fresh object each render would
  // re-run the derivation effect forever.
  const marbleKey = info.marble ? JSON.stringify(info.marble) : ''
  const marble = useMemo<MarbleReveal | undefined>(
    () => (marbleKey ? (JSON.parse(marbleKey) as MarbleReveal) : undefined),
    [marbleKey]
  )
  useEffect(() => {
    // Birdie has no client-side recompute (B33): nothing to derive, nothing
    // that could ever read as verified. The core refuses it as well; this
    // just spares the WebCrypto round-trip.
    if (!settled || !serverSeed || game === 'birdie') return
    let cancelled = false
    void deriveRoundVerification({
      serverSeed,
      game,
      schemeVersion,
      nonce,
      beacon,
      chainRootHash,
      chainIndex,
      observedCommitment,
      multiplier,
      houseEdge,
      counting,
      marble
    }).then((result) => {
      if (!cancelled) setDerivation(result)
    })
    return () => {
      cancelled = true
    }
  }, [
    settled,
    game,
    serverSeed,
    schemeVersion,
    nonce,
    beacon,
    chainRootHash,
    chainIndex,
    observedCommitment,
    multiplier,
    houseEdge,
    counting,
    marble
  ])

  // Focus management: remember the opener, focus the dialog on mount,
  // restore on unmount. (Skipped inline — the harness must not steal focus.)
  useEffect(() => {
    if (inline) return
    const opener = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    return () => opener?.focus?.()
  }, [inline])

  // Esc closes; Tab cycles within the dialog (minimal focus trap).
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], [tabindex]:not([tabindex="-1"])'
      )
      if (focusables.length === 0) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    [onClose]
  )

  if (typeof document === 'undefined') return null

  // The verified verdict: both checks green → verified; any explicit false
  // → mismatch surface (never silently swallow a failed recomputation).
  const verdict =
    derivation === null
      ? null
      : derivation.commitmentMatches === false ||
          derivation.resultMatches === false ||
          derivation.chainLinksToRoot === false
        ? 'mismatch'
        : // "verified" REQUIRES the INDEPENDENTLY-OBSERVED pre-round commitment
          // to match (not a tautological recompute). Reaching this branch
          // already proves the result and (v2) chain link are NOT false — the
          // mismatch branch above excluded them — so commitment is the only
          // remaining gate. commitmentMatches === undefined (race_ready not
          // seen) is honestly unverified — NOT green.
          derivation.commitmentMatches === true
          ? 'verified'
          : null

  // Prefill the public verifier with this round's public values, so the button
  // opens it ready to run — the same hand-off the support page does. Falls back
  // to the bare tool for an unsettled round (no seed to prefill yet).
  //
  // SHARED URL CONTRACT (v2) — the public verifier, operator-api's verifyURL
  // and this CTA use the SAME param names. `game` is the discriminator and is
  // OMITTED for crash (back-compat: the verifier defaults to crash). Crash
  // sends houseEdge + multiplier; counting sends the boundary set instead —
  // houseEdge does NOT transfer, because the counting family keeps its margin
  // in the payout odds table, never in the RNG.
  //
  // Birdie (B33) is the Birdie board's OWN builder, verbatim: `verifierLink`
  // already speaks this contract (`game=birdie`, the marble draw, then the
  // card-and-catalogue terms) and falls back to the bare tool without a seed.
  // It returns FIRST so the crash arm at the bottom — houseEdge + multiplier —
  // can never fire for a putting round.
  const verifierUrl = (() => {
    const base = 'https://marbles-io.github.io/155-fairness-verifier/'
    if (info.game === 'birdie') return verifierLink({ fairness: info.birdie })
    if (!info.serverSeed) return base
    const p = new URLSearchParams()
    if (info.game !== 'crash') p.set('game', info.game)
    p.set('serverSeed', info.serverSeed)
    const commitment = info.observedCommitment ?? info.commitment
    if (commitment) p.set('observedCommitment', commitment)
    if (info.chainRootHash) p.set('chainRootHash', info.chainRootHash)
    if (info.chainIndex != null) p.set('chainIndex', String(info.chainIndex))
    if (info.beacon) p.set('beacon', info.beacon)
    if (info.game === 'vehicle_boundaries') {
      if (info.counting) {
        p.set('finalCount', String(info.counting.finalCount))
        p.set('gap', String(info.counting.gap))
        p.set('outcome', info.counting.outcome)
        p.set('lowerBound', String(info.counting.lowerBound))
        p.set('upperBound', String(info.counting.upperBound))
        p.set('outcomes', info.counting.outcomes.join(','))
        p.set('weightsPpm', info.counting.weightsPpm.join(','))
      }
    } else if (info.game === 'marble_order') {
      // Marbles has no houseEdge/multiplier — its result IS the finishing order.
      // weightsPpm rides along only for the weighted single-winner games.
      if (info.marble) {
        p.set('marbles', info.marble.marbles.join(','))
        p.set('k', String(info.marble.k))
        p.set('order', info.marble.order.join(','))
        p.set('index', String(info.marble.index))
        p.set('draw', info.marble.draw)
        if (info.marble.weightsPpm) p.set('weightsPpm', info.marble.weightsPpm.join(','))
      }
    } else {
      if (info.houseEdge != null) p.set('houseEdge', String(info.houseEdge))
      if (info.multiplier != null) p.set('multiplier', String(info.multiplier))
    }
    return `${base}verify.html?${p.toString()}`
  })()

  // Counting rounds name their outcome; the label is the player-facing name of
  // the winning market, not the raw wire token. `jackpot` is the WIRE name only
  // (it is in OUTCOME_ORDER, the golden vectors and every engine payload, and
  // that list is append-only forever) — players have only ever seen it called
  // "Exact", so that is what every surface says.
  const outcomeLabel = (outcome: CountingOutcome): string => {
    switch (outcome) {
      case 'under':
        return t('Under')
      case 'range':
        return t('Range')
      case 'over':
        return t('Over')
      case 'jackpot':
        return t('Exact')
    }
  }

  // A marble round's result is a FINISHING ORDER — 1st / 2nd / 3rd. Podium games
  // are K=3 and single-winner games K=1, so the ordinal never exceeds 3rd; the
  // numeric fallback is defensive only. Each ordinal is its own full-word key so
  // every locale reads naturally (ordinals are not a numeric suffix elsewhere).
  const ordinalLabel = (position: number): string => {
    switch (position) {
      case 1:
        return t('1st')
      case 2:
        return t('2nd')
      case 3:
        return t('3rd')
      default:
        return `${position}`
    }
  }

  /**
   * The winning market, worded exactly the way the game itself labels it in
   * `race_started.payload.optionLabels` ("Under 9" / "10–11" / "Over 12" /
   * "Exact 9"). The bare outcome token beside a bare bound pair ("Under 17–20")
   * reads as nothing to a player — this is the same sentence the board showed
   * them when they placed the bet.
   */
  const marketLabel = (
    c: {
      outcome: CountingOutcome
      lowerBound: number
      upperBound: number
      outcomes: readonly CountingOutcome[]
    },
    finalCount: number
  ): string => {
    // A 4-way round carries the Exact market, and its range sits STRICTLY
    // between the bounds; a 3-way round's range includes them.
    const strictRange = c.outcomes.includes('jackpot')
    switch (c.outcome) {
      case 'under':
        return t('Under {{bound}}', { bound: c.lowerBound })
      case 'over':
        return t('Over {{bound}}', { bound: c.upperBound })
      case 'range':
        return strictRange
          ? `${c.lowerBound + 1}–${c.upperBound - 1}`
          : `${c.lowerBound}–${c.upperBound}`
      case 'jackpot':
        // Exact wins only when the count landed ON a boundary, so the count IS
        // the winning number — name it rather than offering both bounds.
        return t('Exact {{bound}}', { bound: finalCount })
    }
  }

  // The game family lives on the REVEAL. Before it lands, `info.game` is only
  // the back-compat default ('crash'), so `revealed` gates every claim that
  // differs per family — see the explainer below.
  const revealed = info.serverSeed !== undefined
  const isCounting = info.game === 'vehicle_boundaries'
  const isMarble = info.game === 'marble_order'
  // B33. Like the two above, ONLY ever true once the reveal names the family.
  const isBirdie = info.game === 'birdie'
  // Single-winner marble games (Coin Flip, Fast Lane, Stairpong, Survivor, Fish
  // Tank) reveal k=1 — the round's result IS the winner, so no finishing order
  // and no ordinals. Podium games (Plinko, Snake, Rolling Dunes) reveal k=3.
  // DISPATCH ON k, never on the game key — the reveal carries k, the widget's
  // game predicates do not.
  const isSingleWinnerMarble = isMarble && info.marble?.k === 1
  const countingResult =
    derivation?.result.game === 'vehicle_boundaries' ? derivation.result : null
  const branchDraw = countingResult ? countingBranchDraw(countingResult.draws) : undefined
  const marbleResult = derivation?.result.game === 'marble_order' ? derivation.result : null

  const sheet = (
    <div
      className={`crash-round-modal ${inline ? 'crash-round-modal--inline' : ''}`}
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      {/* Scale wrapper — the entry keyframe on __dialog holds its own
          transform (fill-mode both), so the viewport scale must live on a
          separate, un-animated element or the two would fight. */}
      <div className="crash-round-modal__scale" style={inline ? undefined : scaleStyle}>
        <div
          ref={dialogRef}
          className="crash-round-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crash-round-modal-title"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crash-round-modal__header">
            <div className="crash-round-modal__title" id="crash-round-modal-title">
              <span className="crash-round-modal__title-label">{t('Round')}</span>
              <code className="crash-round-modal__round-id">{info.shortId}</code>
            </div>
            {!isCounting && !isMarble && info.multiplier !== undefined && (
              <span className={`crash-multiplier-pill ${getMultiplierTier(info.multiplier)}`}>
                <span>{info.multiplier.toFixed(2)}x</span>
              </span>
            )}
            {/* Counting rounds have no multiplier — the result IS the winning
                outcome and the boundaries the seed placed around the count. */}
            {isCounting && info.counting && (
              <span className="crash-round-modal__outcome-pill">
                <span>{marketLabel(info.counting, info.counting.finalCount)}</span>
                <span className="crash-round-modal__outcome-pill-bounds">
                  {t('count {{bound}}', { bound: info.counting.finalCount })}
                </span>
              </span>
            )}
            {/* Marble rounds have no multiplier either. Podium (k>1): the header
                shows 1st place with its ordinal chip and the full order sits in
                the result check below. Single-winner (k===1): the result IS the
                winner — the one entrant, no ordinal chip. */}
            {isMarble && info.marble && (
              <span className="crash-round-modal__outcome-pill">
                <span>{info.marble.order[0]}</span>
                {!isSingleWinnerMarble && (
                  <span className="crash-round-modal__outcome-pill-bounds">{ordinalLabel(1)}</span>
                )}
              </span>
            )}
            <button
              type="button"
              className="crash-round-modal__close"
              aria-label={t('Close')}
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          {info.endedAt && (
            <div className="crash-round-modal__when">{formatTimeAgo(info.endedAt, t)}</div>
          )}

          {isBirdie ? (
            /* B33 — the Birdie branch INSIDE the shared chassis. The header,
               the close, the "when" line and the explainer are this modal's;
               the body is the Birdie board's, the very component the Birdie
               sheet mounts, so what a player reads here and what they read
               from a disc is one drawing. It carries its own commitment and
               reveal rows, Birdie's extra terms, and the hand-off pill — so
               the three sections and the CTA below are not drawn for it. */
            <BirdieFairnessBody fairness={info.birdie} t={t} verifierHref={verifierUrl} />
          ) : (
            <>
              {/* The pre-round commitment — published before betting closed. */}
              <div className="crash-round-modal__section">
                <SectionEyebrow>{t('Before the round')}</SectionEyebrow>
                <div className="crash-round-modal__hint">
                  {t('Published before the round — sha256 of the secret server seed')}
                </div>
                {info.commitment ? (
                  <CopyValue
                    label={t('Commitment')}
                    value={info.commitment}
                    copiedText={t('Copied!')}
                    copyLabel={t('Copy')}
                  />
                ) : (
                  <div className="crash-round-modal__pending">
                    {t('No commitment was published for this round')}
                  </div>
                )}
              </div>

              {/* The reveal — only after settlement. */}
              <div className="crash-round-modal__section">
                <SectionEyebrow>{t('Revealed at settlement')}</SectionEyebrow>
                {info.serverSeed ? (
                  <>
                    <CopyValue
                      label={t('Seed')}
                      value={info.serverSeed}
                      copiedText={t('Copied!')}
                      copyLabel={t('Copy')}
                    />
                    {info.nonce && (
                      <CopyValue
                        label={t('Nonce')}
                        value={info.nonce}
                        copiedText={t('Copied!')}
                        copyLabel={t('Copy')}
                      />
                    )}
                    {info.chainRootHash && (
                      <CopyValue
                        label={t('Chain root')}
                        value={info.chainRootHash}
                        copiedText={t('Copied!')}
                        copyLabel={t('Copy')}
                      />
                    )}
                    {info.chainIndex != null && (
                      <CopyValue
                        label={t('Chain index')}
                        value={String(info.chainIndex)}
                        copiedText={t('Copied!')}
                        copyLabel={t('Copy')}
                      />
                    )}
                  </>
                ) : (
                  /* Settled-but-not-yet-revealed: the reveal rides the settle
                     pipeline and can lag by seconds — a spinner + "shortly" set
                     the expectation (the old copy read as "should be here
                     already"). The seed streams in live via the store, so the
                     spinner resolves without a reopen. */
                  <div className="crash-round-modal__pending">
                    {info.settled && (
                      <span aria-hidden="true" className="crash-round-modal__pending-spinner" />
                    )}
                    {info.settled
                      ? t('Revealed shortly after settlement')
                      : t('Revealed after the round')}
                  </div>
                )}
              </div>

              {/* The derivation — Aviator's hex/decimal/result table on OUR math,
                recomputed live from the revealed values. Falls back to the
                static formula note when WebCrypto can't run here. Crash derives a
                multiplier; a counting round derives the weighted outcome and the
                boundaries placed around the measured count. */}
              {info.serverSeed && (
                <div className="crash-round-modal__section">
                  <SectionEyebrow>{t('Result check')}</SectionEyebrow>
                  <div className="crash-round-modal__hint">
                    {isCounting
                      ? t('seed → HMAC → weighted outcome → boundaries')
                      : isMarble
                        ? t('seed → HMAC → finishing order')
                        : t('seed → HMAC → first 52 bits → multiplier curve')}
                  </div>
                  {derivation && (
                    <>
                      {derivation.result.game === 'crash' && (
                        <div className="crash-round-modal__derive-table">
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Hex')}</span>
                            <code className="crash-round-modal__derive-value crash-round-modal__derive-value--hex">
                              {derivation.result.hex13}
                            </code>
                          </div>
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Decimal')}</span>
                            <code className="crash-round-modal__derive-value">
                              {derivation.result.r.toFixed(14)}
                            </code>
                          </div>
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Result')}</span>
                            <code className="crash-round-modal__derive-value crash-round-modal__derive-value--result">
                              {derivation.result.multiplier.toFixed(2)}x
                            </code>
                          </div>
                        </div>
                      )}
                      {countingResult && (
                        <div className="crash-round-modal__derive-table crash-round-modal__derive-table--counting">
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">
                              {t('Outcome draw')}
                            </span>
                            <code className="crash-round-modal__derive-value crash-round-modal__derive-value--hex">
                              {countingResult.draws['outcome']}
                            </code>
                          </div>
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Weights')}</span>
                            <span className="crash-round-modal__derive-value crash-round-modal__derive-weights">
                              {countingResult.outcomes.map((outcome, index) => (
                                <span
                                  key={outcome}
                                  className={`crash-round-modal__weight ${
                                    outcome === countingResult.outcome ? 'is-picked' : ''
                                  }`}
                                >
                                  {outcomeLabel(outcome)}{' '}
                                  {formatWeightPercent(countingResult.weightsPpm[index] ?? 0)}
                                </span>
                              ))}
                            </span>
                          </div>
                          {branchDraw && (
                            <div className="crash-round-modal__derive-row">
                              <span className="crash-round-modal__derive-label">
                                {t('Offset draw')}
                              </span>
                              <code className="crash-round-modal__derive-value crash-round-modal__derive-value--hex">
                                {branchDraw.label} {branchDraw.hex13}
                              </code>
                            </div>
                          )}
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Final count')}</span>
                            <code className="crash-round-modal__derive-value">
                              {info.counting?.finalCount}
                            </code>
                          </div>
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Boundaries')}</span>
                            <code className="crash-round-modal__derive-value">
                              {t('lower {{lower}} · upper {{upper}}', {
                                lower: countingResult.lowerBound,
                                upper: countingResult.upperBound
                              })}
                            </code>
                          </div>
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">
                              {t('Winning market')}
                            </span>
                            <code className="crash-round-modal__derive-value crash-round-modal__derive-value--result">
                              {marketLabel(countingResult, info.counting?.finalCount ?? 0)}
                            </code>
                          </div>
                        </div>
                      )}
                      {marbleResult && (
                        <div className="crash-round-modal__derive-table crash-round-modal__derive-table--counting">
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">{t('Order draw')}</span>
                            <code className="crash-round-modal__derive-value crash-round-modal__derive-value--hex">
                              {marbleResult.draw}
                            </code>
                          </div>
                          {/* Weighted single-winner games publish a distribution;
                              the entrant the draw landed on lights up. Equal games
                              carry no weights, so this row is skipped for them. */}
                          {marbleResult.weightsPpm && (
                            <div className="crash-round-modal__derive-row">
                              <span className="crash-round-modal__derive-label">{t('Weights')}</span>
                              <span className="crash-round-modal__derive-value crash-round-modal__derive-weights">
                                {marbleResult.marbles.map((name, index) => (
                                  <span
                                    key={name}
                                    className={`crash-round-modal__weight ${
                                      name === marbleResult.order[0] ? 'is-picked' : ''
                                    }`}
                                  >
                                    {name}{' '}
                                    {formatWeightPercent(marbleResult.weightsPpm?.[index] ?? 0)}
                                  </span>
                                ))}
                              </span>
                            </div>
                          )}
                          {/* Single-winner (k===1): heading "Winner", the one entrant
                              rendered WITHOUT an ordinal. Podium (k>1): "Finishing
                              order" with 1st / 2nd / 3rd ordinals. Same underlying
                              draw either way — this is purely presentation. */}
                          <div className="crash-round-modal__derive-row">
                            <span className="crash-round-modal__derive-label">
                              {isSingleWinnerMarble ? t('Winner') : t('Finishing order')}
                            </span>
                            <span className="crash-round-modal__derive-value crash-round-modal__derive-weights">
                              {marbleResult.order.map((name, index) => (
                                <span
                                  key={`${index}-${name}`}
                                  className={`crash-round-modal__weight ${index === 0 ? 'is-picked' : ''}`}
                                >
                                  {isSingleWinnerMarble ? (
                                    name
                                  ) : (
                                    <>
                                      {ordinalLabel(index + 1)} {name}
                                    </>
                                  )}
                                </span>
                              ))}
                            </span>
                          </div>
                        </div>
                      )}
                      {verdict === 'verified' && (
                        <div className="crash-round-modal__verdict crash-round-modal__verdict--ok">
                          <span className="crash-round-modal__verdict-mark" aria-hidden>
                            ✓
                          </span>
                          {t('Verified — recomputed in your browser and matching')}
                        </div>
                      )}
                      {verdict === 'mismatch' && (
                        <div className="crash-round-modal__verdict crash-round-modal__verdict--bad">
                          <span className="crash-round-modal__verdict-mark" aria-hidden>
                            !
                          </span>
                          {t('Mismatch — the recomputation does not reproduce this round')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Prominent hand-off to the independent, open-source verifier —
                prefilled with this round so it opens ready to run. Recomputing on
                a tool we don't host is the strongest proof (nothing to trust). */}
              {info.commitment && (
                <a
                  className="crash-round-modal__verify-cta"
                  href={verifierUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Open the open-source verifier ↗')}
                </a>
              )}
            </>
          )}

          {/* "What is provably fair?" — expandable, truthful explainer of OUR
            commit/reveal scheme (no player seeds in our derivation). */}
          <div className="crash-round-modal__section">
            <button
              type="button"
              className="crash-round-modal__learn"
              aria-expanded={explainerOpen}
              onClick={() => setExplainerOpen((open) => !open)}
            >
              <span>{t('What is provably fair?')}</span>
              <span
                className={`crash-round-modal__learn-chevron ${explainerOpen ? 'is-open' : ''}`}
                aria-hidden
              >
                ▾
              </span>
            </button>
            {explainerOpen && (
              <div className="crash-round-modal__explainer">
                <p>
                  {t(
                    'Before betting closes, the game publishes a commitment — the sha256 fingerprint of a secret server seed. That seed is one link in a hash chain whose starting point (the root) was published before the game began, so the outcome is fixed the moment betting closes and cannot be changed after bets are placed.'
                  )}
                </p>
                {/* FAMILY-SPECIFIC claims are withheld until the reveal exists.
                    The family is carried by the reveal (`fairness.game`) and
                    NOTHING else — pre-reveal, `isCounting` is merely the
                    back-compat default, so asserting "the crash point is
                    computed from it" over an in-progress counting round would
                    be a claim the modal cannot back. Everything above and the
                    first three steps below hold for both families, which is
                    exactly the part a player can act on before settlement.
                    BIRDIE (B33) gets the family-neutral text only: it uses
                    the same commitment / hash-chain scheme (its reveal wraps
                    the marble runtime's), so the paragraph above and steps
                    1–3 hold; the family-specific paragraph and derivation
                    step are the RECOMPUTE, which Birdie does not have yet. */}
                {revealed &&
                  !isBirdie &&
                  (isCounting ? (
                    <>
                      <p>
                        {t(
                          'The final count is measured from the clip and published with the result — it is an input, not something the seed decides. The game already knows that count when the round opens, which is exactly why the rest has to be provable: the seed, committed before betting closed, is what picks which market wins, at the odds shown here. So the winning market cannot be chosen to suit the count, and cannot change once bets are in — and you can recompute both yourself.'
                        )}
                      </p>
                      <p>
                        {t(
                          'When the round settles, the seed is revealed. Anyone can replay the two draws it makes, reproduce the same winning market and the same boundaries, and confirm the seed hashes back to the published chain root — without trusting us.'
                        )}
                      </p>
                    </>
                  ) : isMarble ? (
                    <p>
                      {isSingleWinnerMarble
                        ? t(
                            'The result is the winner — a single entrant this round’s seed picks from the ones shown. When the round settles the seed is revealed; it makes a single draw that picks that winner, so it was fixed the moment betting closed. Anyone can replay that one draw, reproduce the same winner, and confirm the seed hashes back to the published chain root — without trusting us.'
                          )
                        : t(
                            'The result is the finishing order — 1st, 2nd, 3rd — of this round’s entrants. When the round settles, the seed is revealed. It makes a single draw that picks that order from the entrants shown, so the order was fixed the moment betting closed. Anyone can replay that one draw, reproduce the exact same order, and confirm the seed hashes back to the published chain root — without trusting us.'
                          )}
                    </p>
                  ) : (
                    <p>
                      {t(
                        'When the round settles, the seed is revealed. The crash point is computed from it, and the seed hashes back to that published chain root — so anyone can recompute the result and confirm it was fixed before betting closed, without trusting us.'
                      )}
                    </p>
                  ))}
                <div className="crash-round-modal__explainer-subtitle">{t('How to check')}</div>
                <ol className="crash-round-modal__explainer-steps">
                  <li>{t('Note the commitment shown before the round starts')}</li>
                  <li>{t('After settlement, copy the revealed seed from this window')}</li>
                  <li>
                    {t(
                      'Check that sha256 of the seed (as text) equals the commitment — and that hashing the seed forward reaches the published chain root'
                    )}
                  </li>
                  {/* Same rule as the paragraphs: the derivation step is the
                      one that differs per family, so it waits for the reveal
                      that names the family. It is also the only step a player
                      cannot perform yet — the seed IS its input. */}
                  {revealed &&
                    !isBirdie &&
                    (isCounting ? (
                      <>
                        <li>
                          {t(
                            'Compute HMAC-SHA256 with the hex-decoded seed as the key over the message :outcome:0 and take the first 13 hex digits. Multiply that value by 1000000, shift right 52 bits, and walk the published weights in order — under, range, over, jackpot (the Exact market) — to land on the winning market.'
                          )}
                        </li>
                        <li>
                          {t(
                            'Repeat with that market’s own offset message — for example :under_offset:0 — and place the boundaries around the published final count, keeping the gap the round published. Every round uses the same gap, so the width never hints at which market won.'
                          )}
                        </li>
                      </>
                    ) : isMarble ? (
                      <li>
                        {t(
                          'Compute HMAC-SHA256 with the hex-decoded seed as the key over the message :order:0 and take the first 13 hex digits as a whole number. For an equal race, list every finishing order of the entrants in the same order the round shows them, then multiply that number by how many orders there are and shift right 52 bits to pick which order won. For a weighted race, multiply by 1000000 instead and walk the published weights in order to pick the single winner.'
                        )}
                      </li>
                    ) : (
                      <li>
                        {t(
                          'Compute HMAC-SHA256 with the hex-decoded seed as the key over the message :crash:0, take the first 13 hex digits and divide by 2^52 to get r — the multiplier is 0.94/(1 − r) (the 94% RTP), rounded to 2 decimals; when that would fall below 1.00x the round crashes at 1.00x'
                        )}
                      </li>
                    ))}
                </ol>
                {/* No result check runs for Birdie, so this must not say so. */}
                {revealed && !isBirdie && (
                  <p className="crash-round-modal__explainer-note">
                    {t('The result check above runs these steps in your browser automatically.')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return inline ? sheet : createPortal(sheet, document.body)
}
